import React from 'react';
import { Sprout, Ear, Blend, BookOpen, Lightbulb, MessageCircle, ChevronRight } from 'lucide-react';

// The Read Right Approach — a signature 6-step methodology section.
// Prepare → Hear → Blend → Read → Understand → Communicate

const steps = [
  {
    num: '01',
    title: 'Prepare',
    description:
      'Develop listening, attention, movement, vocabulary and learning readiness.',
    icon: Sprout,
    accent: 'from-emerald-500 to-teal-500'
  },
  {
    num: '02',
    title: 'Hear',
    description:
      'Help children recognise, distinguish and produce speech sounds accurately.',
    icon: Ear,
    accent: 'from-teal-500 to-cyan-500'
  },
  {
    num: '03',
    title: 'Blend',
    description:
      'Teach children how to combine sounds naturally to form words.',
    icon: Blend,
    accent: 'from-cyan-500 to-sky-500'
  },
  {
    num: '04',
    title: 'Read',
    description:
      'Progress from simple words to phrases, sentences and beginner books.',
    icon: BookOpen,
    accent: 'from-sky-500 to-[#3B4BA0]'
  },
  {
    num: '05',
    title: 'Understand',
    description: 'Develop vocabulary, comprehension and meaning.',
    icon: Lightbulb,
    accent: 'from-[#3B4BA0] to-violet-500'
  },
  {
    num: '06',
    title: 'Communicate',
    description:
      'Build confidence in speaking, reading, writing and expressing ideas.',
    icon: MessageCircle,
    accent: 'from-violet-500 to-rose-500'
  }
];

const ReadRightApproach = () => {
  return (
    <section
      id="read-right-approach"
      data-testid="read-right-approach"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-slate-50/60 to-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block bg-[#3B4BA0]/10 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Our Methodology
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            The Read Right Approach
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Children learn to read successfully when literacy is taught in the
            right developmental sequence. The Read Right Approach combines
            child development, phonemic awareness, structured phonics,
            blending, comprehension and confidence-building.
          </p>
        </div>

        {/* Visual pathway — desktop horizontal, mobile vertical */}
        <div className="hidden lg:flex items-stretch justify-between gap-2 mb-10 relative">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={step.num}>
                <div className="flex-1 group">
                  <div
                    className={`relative bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col`}
                  >
                    <div
                      className={`w-11 h-11 rounded-xl bg-gradient-to-br ${step.accent} text-white flex items-center justify-center shadow-md mb-3`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="text-xs font-bold text-gray-400 tracking-widest mb-1">
                      STEP {step.num}
                    </p>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div className="flex-shrink-0 flex items-center">
                    <ChevronRight
                      className="h-5 w-5 text-slate-300"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile / tablet stacked cards */}
        <div className="lg:hidden grid sm:grid-cols-2 gap-4">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                data-testid={`approach-step-${step.num}`}
                className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-11 h-11 rounded-xl bg-gradient-to-br ${step.accent} text-white flex items-center justify-center shadow-md flex-shrink-0`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 tracking-widest">
                      STEP {step.num}
                    </p>
                    <h3 className="text-lg font-bold text-gray-900">
                      {step.title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mt-3">
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Linear text pathway */}
        <div className="mt-4 text-center">
          <p className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm sm:text-base font-semibold text-gray-500">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <span className="text-[#3B4BA0]">{s.title}</span>
                {idx < steps.length - 1 && (
                  <span className="text-slate-300">→</span>
                )}
              </React.Fragment>
            ))}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReadRightApproach;
